/**
 * Tickets are a lens over conversations, not a second entity.
 *
 * The five states the ticket UI shows are a projection of two backend columns
 * that stay independent: the workflow `status` enum (open / pending / snoozed /
 * resolved) and the `resolution` disposition (`closed`), which is NULL for the
 * normal case. A disposition is orthogonal to status - a closed ticket is also
 * resolved - so it is read first when deriving the state.
 */

export const TICKET_STATE = {
  OPEN: 'open',
  PENDING: 'pending',
  ON_HOLD: 'on_hold',
  SOLVED: 'solved',
  CLOSED: 'closed',
};

export const CONVERSATION_STATUS = {
  OPEN: 'open',
  PENDING: 'pending',
  SNOOZED: 'snoozed',
  RESOLVED: 'resolved',
  ALL: 'all',
};

export const RESOLUTION = {
  CLOSED: 'closed',
};

const STATE_FROM_RESOLUTION = {
  [RESOLUTION.CLOSED]: TICKET_STATE.CLOSED,
};

// The `resolution` the list endpoint is asked for, per state view. `none`
// means "no disposition at all", which is what the four workflow states are:
// a solved ticket that has since been closed belongs in the closed view.
const RESOLUTION_FOR_STATE = {
  [TICKET_STATE.OPEN]: 'none',
  [TICKET_STATE.PENDING]: 'none',
  [TICKET_STATE.ON_HOLD]: 'none',
  [TICKET_STATE.SOLVED]: 'none',
  [TICKET_STATE.CLOSED]: RESOLUTION.CLOSED,
};

const STATE_FROM_STATUS = {
  [CONVERSATION_STATUS.OPEN]: TICKET_STATE.OPEN,
  [CONVERSATION_STATUS.PENDING]: TICKET_STATE.PENDING,
  [CONVERSATION_STATUS.SNOOZED]: TICKET_STATE.ON_HOLD,
  [CONVERSATION_STATUS.RESOLVED]: TICKET_STATE.SOLVED,
};

/**
 * The one frontend reader of ticket state. Prefers the server-derived
 * `ticket_state` (Conversation#ticket_state, the single backend implementation
 * of the same matrix); the local status x resolution projection below is only
 * a fallback for payloads that predate the field (e.g. conversations already
 * in the store from before a deploy). Removing the fallback is a later pass,
 * once every payload the dashboard holds is known to carry the field.
 *
 * @param {{ticket_state: ?string, status: string, resolution: ?string}} conversation
 * @returns {string} one of TICKET_STATE
 */
export const deriveTicketState = ({
  ticket_state: ticketState,
  status,
  resolution,
} = {}) =>
  ticketState || STATE_FROM_RESOLUTION[resolution] || STATE_FROM_STATUS[status];

/**
 * The conversation status a ticket state writes back to. Only the four workflow
 * states are writable from the UI - `closed` needs a resolution write, which
 * the status cell performs separately.
 */
export const STATUS_FOR_STATE = {
  [TICKET_STATE.OPEN]: CONVERSATION_STATUS.OPEN,
  [TICKET_STATE.PENDING]: CONVERSATION_STATUS.PENDING,
  [TICKET_STATE.ON_HOLD]: CONVERSATION_STATUS.SNOOZED,
  [TICKET_STATE.SOLVED]: CONVERSATION_STATUS.RESOLVED,
};

// The narrowest conversation status a state view can ask the list endpoint for.
// `closed` is `resolved` with a disposition.
const FETCH_STATUS_FOR_STATE = {
  ...STATUS_FOR_STATE,
  [TICKET_STATE.CLOSED]: CONVERSATION_STATUS.RESOLVED,
};

export const SENDABLE_STATES = [
  TICKET_STATE.OPEN,
  TICKET_STATE.PENDING,
  TICKET_STATE.ON_HOLD,
  TICKET_STATE.SOLVED,
];

export const STATE_META = {
  [TICKET_STATE.OPEN]: {
    labelKey: 'TICKETS.STATES.OPEN',
    icon: 'i-lucide-circle-dot',
    pillClass: 'bg-n-blue-3 text-n-blue-11',
    iconClass: 'text-n-blue-10',
  },
  [TICKET_STATE.PENDING]: {
    labelKey: 'TICKETS.STATES.PENDING',
    icon: 'i-lucide-circle-dashed',
    pillClass: 'bg-n-amber-3 text-n-amber-11',
    iconClass: 'text-n-amber-10',
  },
  [TICKET_STATE.ON_HOLD]: {
    labelKey: 'TICKETS.STATES.ON_HOLD',
    icon: 'i-lucide-circle-pause',
    pillClass: 'bg-n-iris-3 text-n-iris-11',
    iconClass: 'text-n-iris-10',
  },
  [TICKET_STATE.SOLVED]: {
    labelKey: 'TICKETS.STATES.SOLVED',
    icon: 'i-lucide-circle-check',
    pillClass: 'bg-n-teal-3 text-n-teal-11',
    iconClass: 'text-n-teal-10',
  },
  [TICKET_STATE.CLOSED]: {
    labelKey: 'TICKETS.STATES.CLOSED',
    icon: 'i-lucide-circle',
    pillClass: 'bg-n-alpha-2 text-n-slate-11',
    iconClass: 'text-n-slate-10',
  },
};

// Ticket types, mirroring the `ticket_type` enum on Conversation. A ticket
// that predates the column serializes NULL and reads as `customer` here -
// the one place the default lives.
export const TICKET_TYPE = {
  CUSTOMER: 'customer',
  BACK_OFFICE: 'back_office',
  TRACKER: 'tracker',
};

export const TICKET_TYPE_META = {
  [TICKET_TYPE.CUSTOMER]: {
    labelKey: 'TICKETS.TYPES.CUSTOMER',
    icon: 'i-lucide-user-round',
    badgeClass: 'bg-n-blue-3 text-n-blue-11',
  },
  [TICKET_TYPE.BACK_OFFICE]: {
    labelKey: 'TICKETS.TYPES.BACK_OFFICE',
    icon: 'i-lucide-briefcase',
    badgeClass: 'bg-n-amber-3 text-n-amber-11',
  },
  [TICKET_TYPE.TRACKER]: {
    labelKey: 'TICKETS.TYPES.TRACKER',
    icon: 'i-lucide-git-fork',
    badgeClass: 'bg-n-iris-3 text-n-iris-11',
  },
};

export const ticketTypeOf = conversation =>
  conversation?.ticket_type || TICKET_TYPE.CUSTOMER;

// The system categories every state maps to (Submitted / In progress /
// Waiting on customer / Resolved) - the fixed vocabulary reporting and
// automations see, however the state itself is labelled. Mirrors the ticket
// entries of the chat list's status menu.
export const STATE_CATEGORY = {
  [TICKET_STATE.OPEN]: 'submitted',
  [TICKET_STATE.PENDING]: 'in_progress',
  [TICKET_STATE.ON_HOLD]: 'waiting',
  [TICKET_STATE.SOLVED]: 'resolved',
  [TICKET_STATE.CLOSED]: 'resolved',
};

export const CATEGORY_META = {
  submitted: { labelKey: 'TICKETS.CATEGORIES.SUBMITTED' },
  in_progress: { labelKey: 'TICKETS.CATEGORIES.IN_PROGRESS' },
  waiting: { labelKey: 'TICKETS.CATEGORIES.WAITING' },
  resolved: { labelKey: 'TICKETS.CATEGORIES.RESOLVED' },
};

export const STATE_RANK = {
  [TICKET_STATE.OPEN]: 1,
  [TICKET_STATE.PENDING]: 2,
  [TICKET_STATE.ON_HOLD]: 3,
  [TICKET_STATE.SOLVED]: 4,
  [TICKET_STATE.CLOSED]: 5,
};

export const PRIORITY = {
  URGENT: 'urgent',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

export const PRIORITY_META = {
  [PRIORITY.URGENT]: {
    labelKey: 'TICKETS.PRIORITY.URGENT',
    icon: 'i-lucide-arrow-up',
    toneClass: 'text-n-ruby-11',
    flagged: true,
  },
  [PRIORITY.HIGH]: {
    labelKey: 'TICKETS.PRIORITY.HIGH',
    icon: 'i-lucide-arrow-up',
    toneClass: 'text-n-ruby-11',
    flagged: true,
  },
  [PRIORITY.MEDIUM]: {
    labelKey: 'TICKETS.PRIORITY.MEDIUM',
    icon: 'i-lucide-arrow-up',
    toneClass: 'text-n-amber-11',
    flagged: false,
  },
  [PRIORITY.LOW]: {
    labelKey: 'TICKETS.PRIORITY.LOW',
    icon: 'i-lucide-arrow-down',
    toneClass: 'text-n-slate-10',
    flagged: false,
  },
};

// Conversations carry a null priority far more often than not, so unprioritised
// tickets sort below `low` rather than being dropped from priority sorts.
export const PRIORITY_RANK = {
  [PRIORITY.URGENT]: 4,
  [PRIORITY.HIGH]: 3,
  [PRIORITY.MEDIUM]: 2,
  [PRIORITY.LOW]: 1,
};

export const PRIORITY_FILTERS = [
  { key: 'all', labelKey: 'TICKETS.PRIORITY.ANY' },
  { key: PRIORITY.URGENT, labelKey: 'TICKETS.PRIORITY.URGENT' },
  { key: PRIORITY.HIGH, labelKey: 'TICKETS.PRIORITY.HIGH' },
  { key: PRIORITY.MEDIUM, labelKey: 'TICKETS.PRIORITY.MEDIUM' },
  { key: PRIORITY.LOW, labelKey: 'TICKETS.PRIORITY.LOW' },
];

export const ASSIGNEE_TYPE = {
  ALL: 'all',
  ME: 'me',
  UNASSIGNED: 'unassigned',
};

/**
 * Views are URL query params, not seeded filter records, so a view is a real
 * link that survives a reload. Each one declares the conversation list request
 * it needs (`status` / `assigneeType`) plus the ticket state it narrows to once
 * the payload is in. The dispositions are skipped: they are shelves nothing can
 * put a conversation on any more, so a view onto them would always be empty.
 */
export const TICKET_VIEWS = [
  {
    key: 'all',
    labelKey: 'TICKETS.VIEWS.ALL',
    icon: 'i-lucide-ticket',
    primary: true,
    status: CONVERSATION_STATUS.ALL,
    assigneeType: ASSIGNEE_TYPE.ALL,
  },
  {
    key: 'unassigned',
    labelKey: 'TICKETS.VIEWS.UNASSIGNED',
    icon: 'i-lucide-circle-dashed',
    primary: true,
    status: CONVERSATION_STATUS.ALL,
    assigneeType: ASSIGNEE_TYPE.UNASSIGNED,
  },
  {
    key: 'my_open',
    labelKey: 'TICKETS.VIEWS.MY_OPEN',
    icon: 'i-lucide-user-round',
    primary: true,
    status: CONVERSATION_STATUS.OPEN,
    assigneeType: ASSIGNEE_TYPE.ME,
  },
  ...Object.values(TICKET_STATE).map(state => ({
    key: state,
    labelKey: STATE_META[state].labelKey,
    icon: STATE_META[state].icon,
    primary: false,
    status: FETCH_STATUS_FOR_STATE[state] || CONVERSATION_STATUS.ALL,
    assigneeType: ASSIGNEE_TYPE.ALL,
    resolution: RESOLUTION_FOR_STATE[state],
    state,
  })),
];

export const DEFAULT_VIEW_KEY = 'all';

// The two right-hand panels of the thread. Shared by the thread header tabs and
// the icon rail the collapsed panel leaves behind, so both light up the same
// panel and stay in the same order.
export const PANEL_TABS = [
  {
    key: 'details',
    labelKey: 'TICKETS.THREAD.DETAILS',
    icon: 'i-lucide-user-round',
  },
  {
    key: 'copilot',
    labelKey: 'TICKETS.THREAD.COPILOT',
    icon: 'i-lucide-sparkles',
  },
];

// The tickets lens: only conversations explicitly stamped as tickets
// (`additional_attributes.ticket`, set by CreateLinkedTicketService or any
// new-ticket flow). Mirrors `Conversation.tickets` on the server.
// Filtered/saved-view fetches return all conversations and are narrowed here.
export const isTicketConversation = conversation =>
  Boolean(conversation?.additional_attributes?.ticket);

export const findView = key =>
  TICKET_VIEWS.find(view => view.key === key) ||
  TICKET_VIEWS.find(view => view.key === DEFAULT_VIEW_KEY);
